<template>
    <BaseDialog
        :open="Boolean(state)"
        :title="copy.title"
        :description="copy.body"
        :disable-portal="disablePortal"
        disable-close-on-backdrop-click
        overlay-class="z-[80]"
        dialog-content-class="z-[90] md:w-[480px] shadow-2xl">
        <div class="flex flex-col items-start gap-5 p-6 md:gap-8 md:p-10">
            <h2 class="title" data-testid="session-ended-title">{{ copy.title }}</h2>
            <p class="body text-[#595959]" data-testid="session-ended-body">{{ copy.body }}</p>
            <div class="flex w-full justify-end">
                <BaseButton theme="primary" class="base-btn-dark" data-testid="session-ended-action" @click="copy.action">
                    {{ copy.actionLabel }}
                </BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
/**
 * The single session-ended dialog, mounted at the app root in vue.js rather than inside a layout
 * (BUG-947). LeftSidebarLayout covers well under half the Inertia pages, and a CSRF 403 can be
 * answered from any of them.
 *
 * Opens over whatever page the user is on instead of replacing it, so an unsaved draft is still
 * there if the remedy turns out to be a reload rather than a new login.
 *
 * `open` is bound one-way on purpose: neither remedy leaves the user on this page, so there is no
 * state to return to and nothing should dismiss the dialog. That is also why the backdrop and
 * escape key are disabled and no close affordance is rendered.
 *
 * Deliberately in its own stacking tier, above everything else it can appear over. Everything here
 * is portalled to <body>, so the numbers alone decide who paints on top:
 *
 *   - ordinary dialogs (BaseDialog's defaults): overlay z-20, content z-30
 *   - side panels (GoalEditSidebar, GoalDetailSidebar): z-40 and z-50
 *   - this dialog: overlay z-80, content z-90
 *
 * Both halves have to clear the tier, not just the content. At BaseDialog's defaults this dialog
 * appeared *behind* the goal side panel with its only button clipped, and when raised to share a
 * tier with other dialogs its overlay still landed under their card, leaving an undimmed white panel
 * sitting on another white panel. Being above them dims whatever is open, including another dialog,
 * which is the right message anyway: once the session is gone, everything behind it is moot.
 *
 * Left the shared BaseDialog defaults alone on purpose. Ordinary dialogs still render under those
 * side panels, which is a real but separate problem, and ChatHome already patches it per call site
 * by passing z-60.
 */
import { usePage } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { CSRF_STALE_TOKEN, sessionEndedState } from "~vue/utils/sessionEnded";
import { computed } from "vue";

// reka-ui portals the dialog to <body>, which puts it outside the test wrapper. Mirrors the
// escape hatch ConfirmationDialog exposes for the same reason.
const { disablePortal = false } = defineProps<{ disablePortal?: boolean }>();

const state = sessionEndedState;

const nextUrl = computed(() => globalThis.location.pathname + globalThis.location.search);

/**
 * The branded login page for this user's org, resolved server-side while they were still
 * authenticated and carried in the props of the page already on screen. Preferred over the
 * generic URL in the 403 body: by the time the session is dead there is nothing left for the
 * server to resolve an organization from, and an auto-SSO org sent to the generic page loses
 * its own sign-in route.
 */
function loginUrl(): string {
    return (usePage().props?.session_ended_login_url as string) ?? state.value?.loginUrl ?? "/accounts/login/";
}

function goToLogin() {
    const url = loginUrl();
    const separator = url.includes("?") ? "&" : "?";
    // Replace rather than assign, so the back button can't return to the dead page.
    globalThis.location.replace(`${url}${separator}next=${encodeURIComponent(nextUrl.value)}`);
}

function reloadPage() {
    globalThis.location.reload();
}

const copy = computed(() => {
    // A stale token means the session is alive and only the page's copy of the token is old, so
    // telling this user to log in would be untrue -- a fresh page is what actually fixes it.
    if (state.value?.code === CSRF_STALE_TOKEN) {
        return {
            title: "That didn't go through",
            body: "Your last action wasn't saved. Reload the page and try again.",
            actionLabel: "Reload page",
            action: reloadPage,
        };
    }
    return {
        title: "Your session has expired",
        body: "Your login session has expired. Please log in again to continue.",
        actionLabel: "Log in",
        action: goToLogin,
    };
});
</script>
